import { createElement, type CSSProperties, type ReactNode } from 'react';

/**
 * Backend content-type discriminator ({@code course.pluginId}) → glyph.
 * Ported verbatim from the legacy LMS's {@code getIconPath} type→icon map
 * (same paths, same {@code accountConfig.domain === "HBURADA"} bitmap
 * override for the four types that have one). The assets live under the
 * LMS portal's {@code public/static/img/icons/course-type/} — only that
 * host actually serves them, so `<img>` (not a bundled asset) is the only
 * option from a package mounted by more than one app.
 */
const PLUGIN_ICON_PATHS: Record<string, string> = {
  Pdf: '/static/img/icons/course-type/i-pdf.svg',
  C8Video: '/static/img/icons/course-type/i-cinema8.svg',
  Wowslides: '/static/img/icons/course-type/i-wowslides.svg',
  C8Interactive: '/static/img/icons/course-type/i-cinema8.svg',
  QServExam: '/static/img/icons/course-type/i-exam.svg',
  QServSurvey: '/static/img/icons/course-type/i-survey.svg',
  Program: '/static/img/icons/course-type/i-program.svg',
  EPub: '/static/img/icons/course-type/i-epub.svg',
  Scorm: '/static/img/icons/course-type/i-scorm.svg',
  InClass: '/static/img/icons/course-type/i-face.svg',
  Homework: '/static/img/icons/course-type/i-homework.svg',
  Youtube: '/static/img/icons/course-type/i-video.svg',
  Vimeo: '/static/img/icons/course-type/i-video.svg',
  ZoomMeetings: '/static/img/icons/course-type/i-virtual.svg',
  ZoomWebinar: '/static/img/icons/course-type/i-virtual.svg',
  WebCourse: '/static/img/icons/course-type/i-remote.svg',
  ClickMeeting: '/static/img/icons/course-type/i-ivideo.svg',
  Kaltura: '/static/img/icons/course-type/i-video.svg',
  Marketplace: '/static/img/icons/course-type/i-remote.svg',
  InLearning: '/static/img/icons/course-type/i-inlearning.png',
  Crossknowledge: '/static/img/icons/course-type/i-inlearning.png',
};

/** Tenants on the {@code HBURADA} domain ship their own bitmap for these
 *  four types instead of the shared SVG. */
const HBURADA_PNG_ICONS: Record<string, string> = {
  Pdf: '/static/img/icons/course-type/icon-pdf.png',
  C8Video: '/static/img/icons/course-type/icon-video.png',
  Wowslides: '/static/img/icons/course-type/icon-wowslides.png',
  C8Interactive: '/static/img/icons/course-type/icon-c8-interactive.png',
};

/** Prefixes a host `public/` asset path with its configured base path
 *  (mirrors the LMS portal's own `staticAsset` helper in
 *  `course-detail-client.tsx`) so it resolves under `/portal` in every
 *  environment. */
function staticAsset(path: string): string {
  const base = (process.env.NEXT_PUBLIC_BASE_PATH ?? '').replace(/\/+$/, '');
  return `${base}${path}`;
}

export interface CoursePluginIconProps {
  pluginId: string | undefined;
  /** Tenant's {@code accountConfig.domain} — gates the legacy bitmap
   *  override for the four types that have one. */
  domain?: string;
  size?: number;
  className?: string;
  style?: CSSProperties;
}

/** Footer/badge glyph for a course's {@code pluginId}; renders `null` for
 *  an absent or unrecognized type (caller renders nothing). */
export function CoursePluginIcon({
  pluginId,
  domain,
  size = 24,
  className,
  style,
}: CoursePluginIconProps): ReactNode {
  if (!pluginId) return null;

  const asset =
    (domain === 'HBURADA' ? HBURADA_PNG_ICONS[pluginId] : undefined) ?? PLUGIN_ICON_PATHS[pluginId];
  if (!asset) return null;

  return createElement('img', {
    src: staticAsset(asset),
    alt: '',
    width: size,
    height: size,
    className,
    // Explicit CSS width/height, not just the HTML attributes — a host's
    // global reset (e.g. Tailwind preflight's `img { height: auto }`)
    // overrides the attribute-derived height, which let tall-aspect
    // source SVGs (e.g. i-cinema8.svg) render far taller than `size` and
    // overflow the footer row.
    style: { objectFit: 'contain', flexShrink: 0, width: size, height: size, ...style },
  });
}
